import {
  AdFrequencyControlConfiguration,
  AdFrequencyDecision,
  AdFrequencyDecisionReason,
} from './types';

export const DEFAULT_MINIMUM_CONTENT_CONSUMPTION_IN_MS = 180_000;
export const DEFAULT_AD_BREAK_EXPOSURE_THRESHOLD = 0.66;

export interface SessionState {
  configuration: Required<Pick<AdFrequencyControlConfiguration, 'minimumContentConsumptionInMS' | 'adBreakExposureThreshold' | 'allowFirstOpportunity' | 'enabled'>>;
  consumed: number;
  hasEvaluatedOpportunity: boolean;
  hasQualifyingExposure: boolean;
}

export function validateConfiguration(configuration: AdFrequencyControlConfiguration): void {
  if (configuration.minimumContentConsumptionInMS !== undefined &&
    (!Number.isFinite(configuration.minimumContentConsumptionInMS) || configuration.minimumContentConsumptionInMS < 0)) {
    throw new Error('minimumContentConsumptionInMS must be finite and greater than or equal to zero');
  }
  if (configuration.adBreakExposureThreshold !== undefined &&
    (!Number.isFinite(configuration.adBreakExposureThreshold) ||
      configuration.adBreakExposureThreshold <= 0 ||
      configuration.adBreakExposureThreshold > 1)) {
    throw new Error('adBreakExposureThreshold must be finite, greater than zero, and less than or equal to one');
  }
}

export function normalizeConfiguration(configuration: AdFrequencyControlConfiguration): SessionState['configuration'] {
  validateConfiguration(configuration);
  return {
    minimumContentConsumptionInMS: configuration.minimumContentConsumptionInMS ?? DEFAULT_MINIMUM_CONTENT_CONSUMPTION_IN_MS,
    adBreakExposureThreshold: configuration.adBreakExposureThreshold ?? DEFAULT_AD_BREAK_EXPOSURE_THRESHOLD,
    allowFirstOpportunity: configuration.allowFirstOpportunity ?? true,
    enabled: configuration.enabled ?? false,
  };
}

export function evaluateSession(state: SessionState): AdFrequencyDecision {
  const required = state.configuration.minimumContentConsumptionInMS / 1000;
  if (!state.configuration.enabled) {
    return { allowed: true, reason: 'disabled', consumed: state.consumed, required };
  }
  if (!state.hasEvaluatedOpportunity && state.configuration.allowFirstOpportunity) {
    return { allowed: true, reason: 'first-opportunity', consumed: state.consumed, required };
  }
  if (state.consumed >= required) {
    return { allowed: true, reason: 'threshold-reached', consumed: state.consumed, required };
  }
  return { allowed: false, reason: 'insufficient-content-consumption', consumed: state.consumed, required };
}

export function isDecisionReason(reason: AdFrequencyDecisionReason): boolean {
  return reason.length > 0;
}